import {
	ActionButton,
	ColorArea,
	ColorField,
	ColorSlider,
	ColorSwatch,
	ColorSwatchPicker,
} from '@react-spectrum/s2';
import ChevronDownIcon from '@react-spectrum/s2/icons/ChevronDown';
import ChevronUpIcon from '@react-spectrum/s2/icons/ChevronUp';
import { useState } from 'react';
import { parseColor, type Color } from 'react-aria-components';

type ColorPickerProps = {
	label: string;
	/** Current color as a hex string, e.g. "#e5e5e5". */
	value: string;
	onChange: (hex: string) => void;
	brandColors?: string[] | null;
	/** Whether the full picker is expanded on first render. Defaults to true. */
	defaultExpanded?: boolean;
};

function parseColorSafe(value: string, fallback = 'hsl(0, 0%, 100%)'): Color {
	try {
		return parseColor(value).toFormat('hsl');
	} catch {
		return parseColor(fallback).toFormat('hsl');
	}
}

function colorToHex(color: Color): string {
	return color.toString('hex');
}

/**
 * Compact color picker: shows a ColorField (swatch + hex input) with an "Edit" toggle.
 * Expanding reveals brand swatches (if provided) + ColorArea + ColorSlider.
 *
 * Fully controlled — caller owns the hex color state.
 */
export const ColorPicker = ({
	label,
	value,
	onChange,
	brandColors,
	defaultExpanded = true,
}: ColorPickerProps) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const color = parseColorSafe(value);

	const handleChange = (newColor: Color) => {
		onChange(colorToHex(newColor.toFormat('hsl')));
	};

	return (
		<div className="flex flex-col gap-2">
			<div
				className="text-xs font-medium tracking-wider text-gray-500 uppercase"
			>
				{label}
			</div>

			<div className="flex items-center gap-2">
				<ColorField
					value={color}
					onChange={(c) => c && handleChange(c)}
					aria-label={label}
				/>
				<ActionButton
					isQuiet
					onPress={() => setIsExpanded((v) => !v)}
					aria-label={
						isExpanded ? `Collapse ${label} picker` : `Expand ${label} picker`
					}
				>
					{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</ActionButton>
			</div>

			{isExpanded && (
				<>
					{brandColors && brandColors.length > 0 && (
						<div>
							<div className="mb-1 text-xs text-gray-400">Brand colors</div>
							<ColorSwatchPicker
								value={value}
								onChange={(c) => handleChange(c)}
							>
								{brandColors.map((c) => (
									<ColorSwatch key={c} color={c} />
								))}
							</ColorSwatchPicker>
						</div>
					)}

					<div className="flex flex-col gap-2">
						<ColorArea
							value={color}
							onChange={handleChange}
							xChannel="saturation"
							yChannel="lightness"
						/>
						<ColorSlider value={color} onChange={handleChange} channel="hue" />
					</div>
				</>
			)}
		</div>
	);
};
